import type { TeamNotification as TeamNotificationType } from "@redotech/effect-api-shared/models/team/notification";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { AuthContext } from "@redotech/redo-merchant-app-common/auth";
import { REDO_EFFECT_API_URL } from "@redotech/redo-merchant-app-common/config";
import { createContext, useContext, useMemo, useRef } from "react";
import { useStreamConnection } from "./use-stream-connection";

export type NotificationEventType = TeamNotificationType["_tag"];

// Helper type to get event by tag
export type NotificationEventOfType<T extends NotificationEventType> = Extract<
  TeamNotificationType,
  { _tag: T }
>;

// Generic callback for notification event handlers
// Example:
// NotificationHandler<"messageCreated"> =
//   (event: MessageCreatedNotification) => void
export type NotificationHandler<T extends NotificationEventType> = (
  event: NotificationEventOfType<T>,
) => void;

export interface NotificationStreamContext {
  subscribe: <T extends NotificationEventType>(
    eventType: T,
    callback: NotificationHandler<T>,
  ) => () => void;
  isConnected: boolean;
}

export const NotificationStreamReactContext = createContext<
  NotificationStreamContext | undefined
>(undefined);
NotificationStreamReactContext.displayName = "NotificationStreamContext";

/**
 * Hook to access the notification stream context.
 * Must be used within a NotificationStreamProvider.
 */
export function useNotificationStream(): NotificationStreamContext {
  return useRequiredContext(NotificationStreamReactContext);
}

// Handler function type for internal storage.
// Handlers are stored by eventType and only called when event._tag matches,
// so the cast from NotificationHandler<T> to this type is safe.
type NotificationEventHandler = (event: TeamNotificationType) => void;

/**
 * Hook that manages a single streaming connection to the team notifications endpoint.
 *
 * The endpoint (/api/streams/team/notifications) receives events published
 * via publishNotification() in the backend. When a message is created,
 * the backend publishes to a Redis pub/sub topic, and the endpoint
 * forwards it to all connected clients.
 *
 * This hook:
 * 1. Establishes ONE streaming connection on mount
 * 2. Receives typed notification events (messageCreated, typing, etc.)
 * 3. Notifies all subscribers for the relevant event type
 * 4. Auto-reconnects with exponential backoff on disconnect
 */
export function useNotificationStreamConnection(): NotificationStreamContext {
  const auth = useContext(AuthContext);

  const eventSubscriberRegistry = useRef<
    Map<NotificationEventType, Set<NotificationEventHandler>>
  >(new Map());

  // Broadcasts a notification event to all handlers for that event type
  const notify = useHandler((event: TeamNotificationType) => {
    const handlersForEvent = eventSubscriberRegistry.current.get(event._tag);
    if (handlersForEvent) {
      for (const handler of handlersForEvent) {
        handler(event);
      }
    }
  });

  // subscribes a handler to a notification event
  // returns an unsubscribe/cleanup function for useEffect to call on subscribing
  // component's unmount
  const subscribe = useHandler(
    <T extends NotificationEventType>(
      eventType: T,
      callback: NotificationHandler<T>,
    ): (() => void) => {
      const registry = eventSubscriberRegistry.current;
      let subscribers = registry.get(eventType);

      if (!subscribers) {
        subscribers = new Set();
        registry.set(eventType, subscribers);
      }

      // Cast is unavoidable due to TypeScript's function parameter contravariance:
      // - (narrow) => void is NOT assignable to (wide) => void
      // - But our runtime pub/sub guarantees safety through key-based dispatch
      const notificationEventHandler = callback as NotificationEventHandler;
      subscribers.add(notificationEventHandler);

      // return this to be called in useEffect to unsubscribe when subscribing component unmounts
      const unsubscribe = () => {
        subscribers.delete(notificationEventHandler);
        if (subscribers.size === 0) {
          registry.delete(eventType);
        }
      };

      return unsubscribe;
    },
  );

  const url = useMemo(
    () =>
      new URL(
        "/api/streams/team/notifications",
        REDO_EFFECT_API_URL,
      ).toString(),
    [],
  );

  const { isConnected } = useStreamConnection({
    url,
    token: auth?.token,
    onNotification: notify,
  });

  return { subscribe, isConnected };
}
